import { useEffect, useMemo, useRef } from "react";
import { getTheme } from "../config/themes";

/**
 * Applies a tier-matched visual theme to the document root as CSS variables
 * and sets data-tier on <html> so CSS handles body backgrounds statically.
 *
 * Optimisations vs the previous version:
 *  - lastThemeId ref skips the entire effect when the tier hasn't changed
 *  - useMemo keys on primitive subscription fields, not the object reference
 *  - CSS variables batched inside requestAnimationFrame (one style recalc)
 *  - document.body.style.background removed — CSS [data-tier] rules handle it
 *  - MutationObserver removed — dark/light backgrounds handled by CSS selectors
 *  - data-tier saved to localStorage so index.html can preload it before React
 *
 * Tradie views only — call once at App level, pass returned theme to children.
 *
 * @param {object|null} subscription — return value from useSubscription (or null)
 * @returns {object} theme — the active THEMES entry
 */
export function useTheme(subscription) {
  const fallbackTierKey =
    typeof document !== "undefined"
      ? document.documentElement.getAttribute("data-tier") || "free"
      : "free";
  const theme = useMemo(
    () => getTheme(subscription, fallbackTierKey),
    [subscription, fallbackTierKey]
  );

  const lastThemeId = useRef(null);

  useEffect(() => {
    if (theme.id === lastThemeId.current) return; // same tier — nothing to do
    lastThemeId.current = theme.id;

    const root = document.documentElement;

    // data-tier on <html> activates CSS [data-tier] body background rules
    root.setAttribute("data-tier", theme.id);

    // Persist so the index.html inline script can restore it before React loads
    try { localStorage.setItem("rq_tier", theme.id); } catch { /* storage blocked */ }

    // Batch all CSS variable writes in one rAF — one style recalculation instead of 8
    requestAnimationFrame(() => {
      root.style.setProperty("--theme-accent",           theme.accent);
      root.style.setProperty("--theme-accent-dark",      theme.accentDark);
      root.style.setProperty("--theme-accent-mid",       theme.accentMid);
      root.style.setProperty("--theme-accent-light",     theme.accentLight);
      root.style.setProperty("--theme-accent-glow",      theme.accentGlow);
      root.style.setProperty("--theme-accent-soft",      theme.accentSoft);
      root.style.setProperty("--theme-primary-gradient", theme.primaryGradient);
      root.style.setProperty("--theme-primary-shadow",   theme.primaryShadow);
      root.style.setProperty("--dark-panel-bg", theme.id === "enterprise" ? "#1a1a1a" : "#2C2C2E");
    });
  }, [theme]);

  return theme;
}
